import { parseFigmaFrameReferences } from "@/features/cogo-xyz/figma-token-connection";
import { useStudioStore } from "@/store/studio-store";
import type { SingleActionIntentResponse } from "./mock-chat-service";

const APPLICATION_GENERATE_WORDS =
  /\b(?:generate|build)\b[^.\n]*\bapp(?:lication)?\s*spec\b|appSpec\s*(?:으로|로)?\s*(?:앱|애플리케이션).*생성/i;
const APPLICATION_BRIEF_WORDS =
  /\bcreate\b[^.\n]*\b(?:application|app)\b[^.\n]*\bfrom\s+(?:an?\s+)?brief\b|브리프(?:로|에서)\s*(?:(?:Coogo|Cogo)\s*)?(?:앱|애플리케이션).*생성/i;
const APPLICATION_PATCH_WORDS =
  /\b(?:apply|execute)\b[^.\n]*\b(?:application|app|coogo json|cogo json)\b[^.\n]*\bpatch(?:es)?\b|(?:애플리케이션|앱|Coogo JSON|Cogo JSON)\s*패치.*적용/i;
const BUNDLE_EXPORT_WORDS =
  /\bexport\b[^.\n]*\b(?:(?:coogo|cogo)\s*)?bundle\b|(?:(?:Coogo|Cogo)\s*)?번들.*내보내/i;
const FIGMA_TOKEN_WORDS =
  /\b(?:validate|verify|confirm)\b[^.\n]*\bfigma\s+(?:access\s+)?token\b|Figma\s*(?:access\s*)?token.*(?:검증|확인)/i;
const FIGMA_SOURCE_WORDS =
  /\bimport\b[^.\n]*\b(?:openpencil|\.fig\s+source|figma\s+source)\b|(?:OpenPencil|Figma\s*source|\.fig\s*source).*(?:가져오기|import)/i;
const FIGMA_FRAME_WORDS =
  /\bimport\b[^.\n]*\bfigma\s+(?:frame|node)\b|Figma\s*(?:frame|프레임|node|노드).*(?:가져오기|import)/i;
const GITHUB_PREVIEW_WORDS =
  /\bimport\b[^.\n]*\bgithub\b[^.\n]*\b(?:pull request|pr)\b|GitHub\s*(?:pull request|PR).*(?:가져오기|import)/i;

const CREATE_COGO_APP_REQUIRED = [
  "workspaceId",
  "title",
  "sourceType",
  "inputType",
  "input",
  "target",
] as const;

export function detectApplicationImportIntent(
  content: string,
): SingleActionIntentResponse | null {
  const prompt = content.trim();
  if (!prompt) return null;

  if (APPLICATION_PATCH_WORDS.test(prompt)) {
    return detectPatchIntent(prompt);
  }
  if (FIGMA_TOKEN_WORDS.test(prompt)) {
    return detectFigmaTokenIntent(prompt);
  }
  if (FIGMA_SOURCE_WORDS.test(prompt)) {
    return detectFigmaSourceIntent(prompt);
  }
  if (FIGMA_FRAME_WORDS.test(prompt)) {
    return detectFigmaFrameIntent(prompt);
  }
  if (GITHUB_PREVIEW_WORDS.test(prompt)) {
    return detectGithubPreviewIntent(prompt);
  }
  if (BUNDLE_EXPORT_WORDS.test(prompt)) {
    return detectBundleExportIntent(prompt);
  }
  if (APPLICATION_BRIEF_WORDS.test(prompt)) {
    return detectCreateFromBriefIntent(prompt);
  }
  if (APPLICATION_GENERATE_WORDS.test(prompt)) {
    return detectApplicationGenerationIntent(prompt);
  }
  return null;
}

function detectApplicationGenerationIntent(content: string): SingleActionIntentResponse {
  const parsed = parseJsonObject(content);
  const appSpec = isRecord(parsed?.appSpec) ? parsed.appSpec : parsed;
  if (!isRecord(appSpec) || !Array.isArray(appSpec.pages) || appSpec.pages.length === 0) {
    return clarification(
      content,
      "Provide the complete appSpec JSON with at least one page. I will not invent missing pages or generation input.",
      "최소 1개 page가 포함된 완전한 appSpec JSON을 제공해주세요. 누락된 page나 생성 입력은 임의로 만들지 않습니다.",
    );
  }
  return prepared(
    "Prepared the explicit appSpec for Studio application generation. Confirmation is required before generation starts.",
    "명시된 appSpec으로 Studio 애플리케이션 생성을 준비했습니다. 생성 시작 전 확인이 필요합니다.",
    content,
    "cogo.studio.requestAppGeneration",
    { appSpec },
  );
}

function detectCreateFromBriefIntent(content: string): SingleActionIntentResponse {
  const payload = parseJsonObject(content);
  const missing = CREATE_COGO_APP_REQUIRED.filter(
    (key) => !hasNonEmptyValue(payload?.[key]),
  );
  if (!payload || missing.length > 0) {
    return clarification(
      content,
      `Provide an exact JSON payload with: ${CREATE_COGO_APP_REQUIRED.join(", ")}. Missing: ${missing.join(", ") || "all required input"}.`,
      `정확한 JSON payload를 제공해주세요: ${CREATE_COGO_APP_REQUIRED.join(", ")}. 누락: ${missing.join(", ") || "전체 필수 입력"}.`,
    );
  }
  return prepared(
    "Prepared the explicit brief for Coogo application import. The preview remains unverified until tool and server readback complete.",
    "명시된 brief로 Coogo 애플리케이션 import를 준비했습니다. tool 및 server readback 전에는 preview 완료로 판단하지 않습니다.",
    content,
    "cogo.studio.create_cogo_app",
    payload,
  );
}

function detectPatchIntent(content: string): SingleActionIntentResponse {
  const objectPayload = parseJsonObject(content);
  const arrayPayload = parseJsonArray(content);
  const patches = Array.isArray(objectPayload?.patches)
    ? objectPayload.patches
    : arrayPayload;
  if (!isExplicitPatchList(patches)) {
    return clarification(
      content,
      "Provide a non-empty RFC6902 patch list. Every patch needs an explicit add, remove, or replace op and JSON pointer path.",
      "비어 있지 않은 RFC6902 patch 목록을 제공해주세요. 각 patch에는 명시적인 add/remove/replace op와 JSON pointer path가 필요합니다.",
    );
  }
  return prepared(
    "Prepared the explicit Coogo JSON patch. Studio will validate it and retain the returned inverse for undo.",
    "명시된 Coogo JSON patch를 준비했습니다. Studio가 검증하고 반환된 inverse를 undo용으로 보관합니다.",
    content,
    "cogo.studio.applyAppPatch",
    { patches },
  );
}

function detectBundleExportIntent(content: string): SingleActionIntentResponse {
  const bundleId = extractBundleId(content);
  const format = content.match(
    /\b(cogo_json|react|html|figma|deploy_package)\b/i,
  )?.[1]?.toLowerCase();
  if (!bundleId || !format) {
    return clarification(
      content,
      "Provide the exact bundle ID and one format: cogo_json, react, html, figma, or deploy_package.",
      "정확한 bundle ID와 format(cogo_json, react, html, figma, deploy_package 중 하나)을 알려주세요.",
    );
  }
  return prepared(
    "Prepared the exact bundle export. A returned artifact reference is not a live-download proof.",
    "정확한 bundle export를 준비했습니다. 반환되는 artifact reference 자체는 live download 증명이 아닙니다.",
    content,
    "cogo.studio.export_cogo_bundle",
    { bundleId, format },
  );
}

function detectFigmaTokenIntent(content: string): SingleActionIntentResponse {
  const figmaAccessToken = labeledValue(
    content,
    /\bfigma\s+(?:access\s+)?token\b|Figma\s*(?:access\s*)?token/i,
  );
  if (!figmaAccessToken) {
    return clarification(
      content,
      "Provide the Figma access token through the secure token input. I will not infer, reuse, or echo a credential.",
      "보안 token 입력으로 Figma access token을 제공해주세요. credential을 추정·재사용·반복 표시하지 않습니다.",
    );
  }
  const workspaceId = extractWorkspaceId(content);
  return prepared(
    "Prepared Figma token validation without showing the credential. Saving on the server stays unconfirmed until it is checked.",
    "credential을 표시하지 않고 Figma token 검증을 준비했습니다. 확인 전에는 server 저장을 완료로 판단하지 않습니다.",
    content,
    "cogo.studio.validate_figma_token",
    { figmaAccessToken, ...(workspaceId ? { workspaceId } : {}) },
  );
}

function detectFigmaSourceIntent(content: string): SingleActionIntentResponse {
  const parsed = parseJsonObject(content);
  const sourceBundle = isRecord(parsed?.sourceBundle)
    ? parsed.sourceBundle
    : undefined;
  if (!sourceBundle) {
    return clarification(
      content,
      "Attach the complete sourceBundle JSON. A Figma URL alone is not enough to read the design.",
      "완전한 sourceBundle JSON을 첨부해주세요. Figma URL만으로 디자인 내용을 임의 생성하지 않습니다.",
    );
  }
  return prepared(
    "Prepared the exact design source bundle for import. This records what the design contains; it does not generate anything.",
    "정확한 디자인 source bundle import를 준비했습니다. 디자인 내용을 기록하는 것이며 생성이 아닙니다.",
    content,
    "cogo.studio.import_openpencil_figma_source",
    {
      sourceBundle,
      ...(typeof parsed?.openInSourceSurface === "boolean"
        ? { openInSourceSurface: parsed.openInSourceSurface }
        : {}),
    },
  );
}

function detectFigmaFrameIntent(content: string): SingleActionIntentResponse {
  const refs = parseFigmaFrameReferences(content);
  if (refs.length !== 1) {
    return clarification(
      content,
      refs.length > 1
        ? "Provide exactly one Figma frame URL per request so the target is unambiguous."
        : "Provide an exact Figma design/file URL containing node-id.",
      refs.length > 1
        ? "대상이 모호하지 않도록 요청마다 Figma frame URL을 정확히 1개만 제공해주세요."
        : "node-id가 포함된 정확한 Figma design/file URL을 제공해주세요.",
    );
  }
  const workspaceId = extractWorkspaceId(content);
  if (!workspaceId) {
    return clarification(
      content,
      "Provide the destination workspace ID for this Figma frame import.",
      "이 Figma frame을 가져올 대상 workspace ID를 알려주세요.",
    );
  }
  const credential = useStudioStore.getState().figmaSourceCredential;
  const figmaTokenRef =
    labeledValue(content, /\bfigma\s+token\s+ref(?:erence)?\b|Figma\s*token\s*ref/i) ??
    (credential?.workspaceId === workspaceId ? credential.tokenRef : undefined);
  if (!figmaTokenRef) {
    return clarification(
      content,
      "Validate a Figma token first or provide its exact token reference. I will not guess or copy a raw token into the import.",
      "먼저 Figma token을 검증하거나 정확한 token reference를 제공해주세요. raw token을 추정하거나 import에 복사하지 않습니다.",
    );
  }
  const createPreview = explicitPreviewChoice(content);
  if (createPreview === null) {
    return clarification(
      content,
      "Say explicitly whether to create a preview for this Figma frame import.",
      "이 Figma frame import에서 preview를 생성할지 명시해주세요.",
    );
  }
  const ref = refs[0]!;
  return prepared(
    "Prepared one exact Figma frame import. Preview and artifact success remain pending tool readback.",
    "정확한 Figma frame 1개 import를 준비했습니다. preview 및 artifact 성공은 tool readback 전까지 미확정입니다.",
    content,
    "cogo.studio.import_figma_frame",
    {
      workspaceId,
      figmaFileId: ref.figmaFileId,
      figmaNodeId: ref.figmaNodeId,
      createPreview,
      figmaTokenRef,
      templateSelection: {
        userIntent: content.trim(),
        scope: "template",
      },
      ...(ref.figmaUrl ? { figmaUrl: ref.figmaUrl } : {}),
    },
  );
}

function detectGithubPreviewIntent(content: string): SingleActionIntentResponse {
  const githubUrl = content.match(
    /https?:\/\/(?:www\.)?github\.com\/([^/\s]+)\/([^/\s]+)\/pull\/(\d+)/i,
  );
  if (!githubUrl) {
    return clarification(
      content,
      "Provide an exact GitHub pull-request URL, for example https://github.com/owner/repo/pull/123.",
      "정확한 GitHub pull-request URL을 제공해주세요. 예: https://github.com/owner/repo/pull/123",
    );
  }
  const workspaceId = extractWorkspaceId(content);
  if (!workspaceId) {
    return clarification(
      content,
      "Provide the destination workspace ID for this GitHub preview import.",
      "이 GitHub preview를 가져올 대상 workspace ID를 알려주세요.",
    );
  }
  return prepared(
    "Prepared the exact GitHub PR preview import. This reads GitHub context and does not write to the pull request.",
    "정확한 GitHub PR preview import를 준비했습니다. GitHub context를 읽지만 pull request에는 쓰지 않습니다.",
    content,
    "cogo.studio.import_github_pr_preview",
    {
      workspaceId,
      repo: `${githubUrl[1]}/${githubUrl[2]}`,
      prNumber: Number(githubUrl[3]),
    },
  );
}

function prepared(
  english: string,
  korean: string,
  content: string,
  type: string,
  payload: Record<string, unknown>,
): SingleActionIntentResponse {
  return {
    text: /[가-힣]/.test(content) ? korean : english,
    actions: [{ type, payload }],
  };
}

function clarification(
  content: string,
  english: string,
  korean: string,
): SingleActionIntentResponse {
  return { text: /[가-힣]/.test(content) ? korean : english };
}

function parseJsonObject(content: string): Record<string, unknown> | null {
  const start = content.indexOf("{");
  const end = content.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  try {
    const parsed = JSON.parse(content.slice(start, end + 1)) as unknown;
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function parseJsonArray(content: string): unknown[] | null {
  const start = content.indexOf("[");
  const end = content.lastIndexOf("]");
  if (start < 0 || end <= start) return null;
  try {
    const parsed = JSON.parse(content.slice(start, end + 1)) as unknown;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasNonEmptyValue(value: unknown): boolean {
  return typeof value === "string" ? value.trim().length > 0 : value != null;
}

function isExplicitPatchList(value: unknown): value is Record<string, unknown>[] {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((entry) => {
      if (!isRecord(entry)) return false;
      if (entry.op !== "add" && entry.op !== "remove" && entry.op !== "replace") {
        return false;
      }
      if (typeof entry.path !== "string" || !entry.path.startsWith("/")) {
        return false;
      }
      return entry.op === "remove" || Object.hasOwn(entry, "value");
    })
  );
}

function labeledValue(content: string, label: RegExp): string | undefined {
  const match = content.match(
    new RegExp(
      `(?:${label.source})\\s*(?:is|=|:|은|는)?\\s*["'\\\`]?(\\S+?)["'\\\`]?(?=\\s|,|$)`,
      label.flags.replace("g", ""),
    ),
  );
  return match?.[1]?.trim().replace(/[.,;]+$/, "") || undefined;
}

function extractWorkspaceId(content: string): string | undefined {
  return labeledValue(
    content,
    /\bworkspace(?:\s*id)?\b|workspaceId|워크스페이스\s*(?:ID|아이디)?/i,
  );
}

function extractBundleId(content: string): string | undefined {
  const explicit = content.match(
    /(?:\bbundle\s+id\b|번들\s*(?:ID|아이디))\s*(?:is|=|:|은|는)?\s*["'`]?([A-Za-z0-9._-]+)["'`]?/i,
  )?.[1];
  if (explicit) return explicit;
  return content.match(
    /\bbundle\b\s*(?:is|=|:|은|는)?\s*["'`]?([A-Za-z0-9._-]+)["'`]?/i,
  )?.[1];
}

function explicitPreviewChoice(content: string): boolean | null {
  if (/\b(?:without|no)\s+preview\b|preview\s*(?:없이|생성\s*안)/i.test(content)) {
    return false;
  }
  if (/\b(?:create|with|generate)\s+(?:a\s+)?preview\b|preview\s*(?:생성|포함)/i.test(content)) {
    return true;
  }
  return null;
}
